import type { AuthResponse } from "~/composables/useAuth"

/**
 * Browser-mode equivalent of Rust's `SessionManager` (`src-tauri/src/api/session.rs`) — there's no
 * SQLite/OS keychain available to a plain web page, so the OAuth session just lives in
 * `localStorage`. Unlike `SessionManager` this does not auto-refresh in the background; refresh
 * happens lazily the next time `getAccessToken()` sees an expired token (see `restClient.ts`).
 */
const STORAGE_KEY = "iopta:browser-session"

interface StoredSession extends AuthResponse {
  /** `Date.now()`-relative expiry, computed from `expires_in` at write time. */
  expires_at: number
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== "undefined"
}

export function saveSession(auth: AuthResponse): void {
  if (!hasLocalStorage()) return
  const stored: StoredSession = { ...auth, expires_at: Date.now() + auth.expires_in * 1000 }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
}

export function loadSession(): StoredSession | null {
  if (!hasLocalStorage()) return null
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredSession
  } catch {
    localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

export function clearSession(): void {
  if (!hasLocalStorage()) return
  localStorage.removeItem(STORAGE_KEY)
}

/** True once the token is within 30s of expiry (mirrors `SessionManager`'s own refresh margin). */
export function isSessionExpiring(session: StoredSession): boolean {
  return Date.now() >= session.expires_at - 30_000
}
